// External libraries
import Grid from '@mui/material/Grid2';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import { Alert, Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

// API
import { useCreateNumberRangeMutation, useUpdateNumberRangeMutation } from '@api/number-ranges/number-ranges-api';

// Models
import { NumberRangeApiModel } from '@models/ApiModels/number-range-api-model';
import { NumberRange } from '@models/DomainModels/Master/number-range';

// Common components
import { CdfFormField } from '@commonComponents/forms/fields/CdfFormField';
import { EntityDetailDialog, EntityDetailDialogWrapper } from '@commonComponents/dialogs/EntityDetailDialog';

import { DynamicStore } from '@state/interfaces/idynamic-store';
import { selectIsBrzFinanceEnabled } from '@state/app-context-slice';

import { FormFactory } from '@services/validation/form-factory';
import { useDecoratorForm } from '@hooks/useDecoratorForm';
import { useEntityDetail, EntityDialogCloseResult } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';

import { useFetchToken } from '@hooks/useFetchToken';
import { useGlobalNumberRangesPermissions } from '@hooks/permissions/useGlobalNumberRangesPermissions';

import { NumberRangeScope } from '../../../../models/enums/NumberRangeScope';

const CROSS_ORG_ALLOWED_SCOPES = new Set<NumberRangeScope>([
  NumberRangeScope.Article,
  NumberRangeScope.Device,
  NumberRangeScope.LabourValue
]);

type FieldValue = string | number | boolean;

type NumberRangeDetailDialogProps = {
  numberRangeData?: NumberRange;
  organizationId: string;
  open: boolean;
  onClose: (result: EntityDialogCloseResult<NumberRange>) => void;
  store: DynamicStore | undefined;
  token: string;
  readPermission?: boolean;
  writePermission?: boolean;
};

export function NumberRangeDetailDialog({
  numberRangeData,
  organizationId,
  open,
  onClose,
  token,
  readPermission = false,
  writePermission = false
}: Readonly<NumberRangeDetailDialogProps>) {
  const { t } = useTranslation(['numberRanges', 'common', 'enums']);

  // Get BRZ Finance state from Redux store
  const isBrzFinanceEnabled = useSelector(selectIsBrzFinanceEnabled);

  // Global number ranges permissions
  const { writePermission: globalWritePermission } = useGlobalNumberRangesPermissions(organizationId);

  // RTK Query hooks
  const [createNumberRange] = useCreateNumberRangeMutation();
  const [updateNumberRange] = useUpdateNumberRangeMutation();

  // Global number range = organizationId is null on the existing record.
  // The backend never returns `isCrossOrganizationData` (it's [JsonIgnore]d server-side),
  // so it must be derived from `organizationId` instead.
  const isGlobalNumberRange = numberRangeData?.organizationId === null;

  // Initialize form using FormFactory
  const formConfig = FormFactory.getFormConfig(
    NumberRangeApiModel,
    numberRangeData
      ? { ...numberRangeData, isCrossOrganizationData: isGlobalNumberRange }
      : {
          scope: '',
          name: '',
          format: '',
          lastNumberUsed: 0,
          resetLastNumberUsed: false,
          isCrossOrganizationData: false
        },
    { showSnackbarOnError: true }
  );

  const {
    data: numberRange,
    errors: validationErrors,
    isValid: formIsValid,
    updateField,
    validateField,
    validateForm,
    resetForm,
    setData,
    setFormChanged,
    isDirty
  } = useDecoratorForm(formConfig.modelClass, formConfig.initialData, formConfig.options);

  // Memoize the isDirty value to prevent excessive re-renders
  const isDirtyValue = useMemo(() => isDirty(), [isDirty]);

  // Check for required fields having values
  // This ensures the save button is disabled until all required fields have values
  const hasRequiredFieldValues = useMemo(() => {
    return (
      // Check for scope (required)
      !!numberRange.scope?.trim() &&
      // Check for name (required)
      !!numberRange.name?.trim() &&
      // Check for format (required)
      !!numberRange.format?.trim()
      // lastNumberUsed is not required as it has a default value of 0
    );
  }, [numberRange.scope, numberRange.name, numberRange.format]);

  // Combine validations - the form is valid only when all required fields are filled AND validation passes
  const isValid = useMemo(() => {
    return formIsValid && hasRequiredFieldValues;
  }, [formIsValid, hasRequiredFieldValues]);

  // Determine if BRZ Finance restriction applies
  const isBrzFinanceRestricted = useMemo(() => {
    return isBrzFinanceEnabled && numberRange.scope === NumberRangeScope.BusinessPartner;
  }, [isBrzFinanceEnabled, numberRange.scope]);

  // Override write permission when BRZ Finance restriction applies or the record is global
  // and the user lacks GlobalNumberRanges.Change permission
  const effectiveWritePermission = useMemo(() => {
    if (!writePermission) return false;
    if (isBrzFinanceRestricted) return false;
    if (isGlobalNumberRange && !globalWritePermission) return false;
    return true;
  }, [writePermission, isBrzFinanceRestricted, isGlobalNumberRange, globalWritePermission]);

  // Create an enhanced wrapper for validateForm with better logging and error handling
  const validateFormWrapper = useCallback(async (): Promise<boolean> => {
    // Run the decorator-based validation
    return (await validateForm()).isValid;
  }, [validateForm]);

  const createEntity = useCallback(async (): Promise<NumberRange> => {
    const createdId = await createNumberRange({
      organizationId,
      numberRange: {
        scope: numberRange.scope ?? '',
        name: numberRange.name ?? '',
        format: numberRange.format ?? '',
        resetLastNumberUsed: numberRange.resetLastNumberUsed ?? false,
        lastNumberUsed: numberRange.lastNumberUsed ?? 0,
        isCrossOrganizationData: numberRange.isCrossOrganizationData ?? false,
        organizationId: numberRange.isCrossOrganizationData ? null : organizationId
      },
      token
    }).unwrap();
    return { ...numberRange, id: createdId, organizationId: numberRange.isCrossOrganizationData ? null : organizationId } as NumberRange;
  }, [createNumberRange, organizationId, token, numberRange]);

  const updateEntity = useCallback(async (): Promise<NumberRange> => {
    await updateNumberRange({
      organizationId,
      numberRangeId: numberRangeData?.id ?? '',
      numberRange: {
        scope: numberRange.scope ?? '',
        name: numberRange.name ?? '',
        format: numberRange.format ?? '',
        resetLastNumberUsed: numberRange.resetLastNumberUsed ?? false,
        lastNumberUsed: numberRange.lastNumberUsed ?? 0,
        isCrossOrganizationData: numberRange.isCrossOrganizationData ?? false,
        organizationId: numberRange.isCrossOrganizationData ? null : organizationId
      },
      token
    }).unwrap();
    return { ...numberRange, id: numberRangeData?.id ?? '', organizationId: numberRange.isCrossOrganizationData ? null : organizationId } as NumberRange;
  }, [updateNumberRange, organizationId, numberRangeData?.id, token, numberRange]);

  const handleDialogClose = useCallback(
    (result: EntityDialogCloseResult<NumberRange>) => {
      resetForm();
      setFormChanged(false);
      onClose(result);
    },
    [onClose, resetForm, setFormChanged]
  );

  const { handleSave, loadingState } = useEntityDetail<NumberRange>({
    entityName: t('entityName'),
    initialData: numberRange as NumberRange,
    open,
    onClose: handleDialogClose,
    createEntity,
    updateEntity,
    validateForm: validateFormWrapper,
    setData: data => setData(data as Partial<NumberRange>, true),
    resetForm,
    loggerContext: 'NumberRangeDetailDialog',
    readPermission,
    writePermission: effectiveWritePermission,
    setFormChanged
  });

  const convertToNumber = useCallback((value: FieldValue): number => {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      return value === '' ? 0 : Number(value.replace(',', '.')) || 0;
    }
    return 0;
  }, []);

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: string, value: FieldValue) => {
      const processedValue = field === 'lastNumberUsed' ? convertToNumber(value) : value;
      updateField(field, processedValue);
      // Auto-uncheck isCrossOrganizationData when scope changes to a non-allowed value
      if (field === 'scope' && !CROSS_ORG_ALLOWED_SCOPES.has(value as NumberRangeScope)) {
        updateField('isCrossOrganizationData', false);
      }
      setTimeout(() => validateField(field, processedValue), 0);
    },
    [updateField, validateField, convertToNumber]
  );

  // Handle field blur (validation)
  const handleFieldBlur = useCallback(
    (field: string) => {
      const currentValue = numberRange[field as keyof NumberRangeApiModel];
      const processedValue = field === 'lastNumberUsed' ? convertToNumber(currentValue as FieldValue) : currentValue;
      validateField(field, processedValue);
    },
    [validateField, numberRange, convertToNumber]
  );

  // Whether the currently selected scope permits the cross-org flag
  const isCrossOrgAllowedForScope = useMemo(
    () => CROSS_ORG_ALLOWED_SCOPES.has(numberRange.scope as NumberRangeScope),
    [numberRange.scope]
  );

  // Create array of available text types from enum
  const availableScopes = useMemo(
    () =>
      Object.entries(NumberRangeScope).map(([key, value]) => ({
        key,
        value
      })),
    []
  );

  // Create form content
  const formContent = (
    <Grid container size={12}>
      {/* BRZ Finance Restriction Warning */}
      {isBrzFinanceRestricted && (
        <Grid size={12}>
          <Box sx={{ mb: 2 }}>
            <Alert severity="warning" data-testid="brz-finance-restriction-alert">
              {t('detailDialog.brzFinanceRestriction')}
            </Alert>
          </Box>
        </Grid>
      )}

      <CdfFormField
        name="scope"
        label={t('detailDialog.scope')}
        value={numberRange.scope ?? ''}
        required
        select
        onChange={handleFieldChange}
        onBlur={handleFieldBlur}
        errors={validationErrors}
        data-testid="scope-field"
        slotProps={{
          input: {
            'aria-label': t('detailDialog.scope'),
            readOnly: !effectiveWritePermission
          }
        }}
        sx={{ '& .MuiFormHelperText-root': { display: 'none' } }}
        fullWidth>
        {availableScopes.map(option => (
          <MenuItem key={option.key} value={option.value}>
            {t(`enums:numberRangeScope.${option.key}`)}
          </MenuItem>
        ))}
      </CdfFormField>

      {/* Cross-Organization (Global) Field */}
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={numberRange.isCrossOrganizationData ?? false}
              onChange={e => handleFieldChange('isCrossOrganizationData', e.target.checked)}
              name="isCrossOrganizationData"
              data-testid="is-cross-organization-data-field"
              disabled={!globalWritePermission || !effectiveWritePermission || !isCrossOrgAllowedForScope}
            />
          }
          label={t('detailDialog.isCrossOrganizationData')}
        />
        <Typography variant="caption" color="text.disabled" display="block" sx={{ ml: '14px', mt: '-4px', mb: '4px' }}>
          {!isCrossOrgAllowedForScope ? t('detailDialog.isCrossOrganizationDataScopeHint') : '\u00a0'}
        </Typography>
      </Grid>

      {/* Name Field */}
      <Grid size={12}>
        <CdfFormField
          name="name"
          label={t('detailDialog.name')}
          value={numberRange.name}
          required
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          errors={validationErrors}
          data-testid="name-field"
          slotProps={{
            input: {
              'aria-label': t('detailDialog.name'),
              readOnly: !effectiveWritePermission
            }
          }}
        />
      </Grid>

      {/* Format Field */}
      <Grid size={12}>
        <CdfFormField
          name="format"
          label={t('detailDialog.format')}
          value={numberRange.format}
          required
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          errors={validationErrors}
          data-testid="format-field"
          helperText={t('detailDialog.formatHelperText')}
          slotProps={{
            input: {
              'aria-label': t('detailDialog.format'),
              readOnly: !effectiveWritePermission
            }
          }}
        />
      </Grid>

      {/* Reset Last Number Used Field */}
      {!isBrzFinanceEnabled && numberRange.scope === NumberRangeScope.BusinessPartner && (
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={numberRange.resetLastNumberUsed ?? false}
                onChange={e => handleFieldChange('resetLastNumberUsed', e.target.checked)}
                name="resetLastNumberUsed"
                data-testid="reset-last-number-used-field"
                readOnly={!effectiveWritePermission}
              />
            }
            label={t('detailDialog.resetLastNumberUsed')}
          />
        </Grid>
      )}

      {/* Last Number Used Field */}
      <Grid size={12}>
        <CdfFormField
          name="lastNumberUsed"
          label={t('detailDialog.lastNumberUsed')}
          value={numberRange.lastNumberUsed}
          type="number"
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          errors={validationErrors}
          data-testid="last-number-used-field"
          slotProps={{
            input: {
              'aria-label': t('detailDialog.lastNumberUsed'),
              readOnly: !effectiveWritePermission
            }
          }}
        />
      </Grid>
    </Grid>
  );

  // Get save button text based on edit vs. create mode
  const saveButtonText = numberRangeData?.id ? t('saveChanges') : t('addButtonText');

  // Get cancel button text based on write permission
  const cancelButtonText = effectiveWritePermission ? t('common:cancel') : t('common:close');

  // Get edit mode display title based on permissions
  const editModeTitle = effectiveWritePermission ? t('edit') : t('view');

  // Get title text based on whether we're editing or creating
  const titleText = numberRangeData?.id ? editModeTitle : t('add');

  return (
    <EntityDetailDialog
      open={open}
      onClose={() => handleDialogClose({ action: 'cancelled' })}
      title={titleText}
      formContent={formContent}
      saveButtonText={saveButtonText}
      cancelButtonText={cancelButtonText}
      readPermission={readPermission}
      writePermission={effectiveWritePermission}
      isLoading={false}
      loadingState={loadingState}
      loadingMessage={t('detailDialog.loadingMessage')}
      savingMessage={t('detailDialog.savingMessage')}
      isValid={isValid}
      isDirty={isDirtyValue}
      onSave={handleSave}
      dataTestId="number-range-detail-dialog"
      maxWidth="xs"
    />
  );
}

type NumberRangeDetailDialogWrapperProps = {
  numberRangeData?: NumberRange;
  organizationId: string;
  open: boolean;
  onClose: (result: EntityDialogCloseResult<NumberRange>) => void;
  store: DynamicStore | undefined;
};

export function NumberRangeDetailDialogWrapper({
  numberRangeData,
  open,
  onClose,
  organizationId,
  store
}: Readonly<NumberRangeDetailDialogWrapperProps>) {
  const token = useFetchToken();

  return (
    <EntityDetailDialogWrapper
      organizationId={organizationId}
      store={store}
      loggerContext="NumberRangeDetailDialog"
      dataTestId="number-range-detail-error-boundary-wrapper"
      entityType="NumberRanges"
      renderContent={permissions => (
        <NumberRangeDetailDialog
          numberRangeData={numberRangeData}
          open={open}
          onClose={onClose}
          organizationId={organizationId}
          store={store}
          token={token}
          readPermission={permissions.readPermission}
          writePermission={permissions.writePermission}
        />
      )}
    />
  );
}
